import { redirectToShellHome } from "./redirectToShell";
import { forceRefreshToken, getValidAccessTokenFromStorage } from "./tokenStorage";
import { THROTTLE_STATUS, THROTTLED_MESSAGE, throttleFieldsFrom } from "./throttle";
import { isWorkspacePausedBody, reportWorkspacePaused } from "./workspacePaused";

export class SuiteApiError extends Error {
  /** Seconds from the `Retry-After` header (a 429 usually carries one). */
  public retryAfter?: number;
  /** `Date.now()` when the failed response arrived. */
  public receivedAt: number;

  constructor(
    message: string,
    public code: number,
    public details?: unknown,
    { retryAfter, receivedAt }: { retryAfter?: number; receivedAt?: number } = {}
  ) {
    super(message);
    this.name = "SuiteApiError";
    this.retryAfter = retryAfter;
    this.receivedAt = receivedAt ?? Date.now();
  }
}

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

/**
 * JSON HTTP client for satellite SPAs (Bearer from shared localStorage + refresh).
 *
 * 401 handling: a 401 on a non-`skipAuth` request indicates the SPA's session
 * is gone (token expired/revoked). We redirect to the shell login with `?next=`
 * carrying the current URL so the user lands back where they were after auth.
 * Skip-auth requests (public endpoints, guest flows) bypass the redirect.
 *
 * 403 handling: if the response body contains both "access token" and "expired"
 * the client forces a token refresh and retries the request once. Ordinary 403s
 * (permission denied, missing role, tenant restriction) are NOT retried —
 * they surface as SuiteApiError to the caller.
 */
export class SuiteApiClient {
  constructor(private baseUrl: string) {}

  private async authHeaders(): Promise<HeadersInit> {
    const token = await getValidAccessTokenFromStorage();
    if (!token) throw new SuiteApiError("No access token available", 401);
    return { Authorization: `Bearer ${token}` };
  }

  private async handleResponse<T>(
    response: Response,
    skipAuth?: boolean,
    parseOk?: (response: Response) => Promise<T>
  ): Promise<T> {
    if (!response.ok) {
      if (response.status === 401 && !skipAuth && typeof window !== "undefined") {
        redirectToShellHome(window.location.href);
      }
      const throttle = throttleFieldsFrom(response);
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      let errorDetails: unknown;
      try {
        const errorData = await response.json();
        const d = errorData as {
          message?: string;
          detail?: string;
          error?: string;
          errors?: { detail?: string }[];
        };
        errorMessage = d.message
          || d.detail
          || d.error
          || d.errors?.[0]?.detail
          || errorMessage;
        errorDetails = errorData;
        // A paused workspace is the session's problem, not this request's:
        // the shell decides what happens next (billing-only screen or sign-out).
        if (response.status === 403 && !skipAuth && isWorkspacePausedBody(errorData)) {
          reportWorkspacePaused(typeof d.detail === "string" ? d.detail : undefined);
        }
      } catch {
        /* non-JSON */
      }
      // DRF's "Request was throttled. Expected available in N seconds." is
      // never a user-facing message (#1747 D1); the raw body stays in details.
      if (response.status === THROTTLE_STATUS) errorMessage = THROTTLED_MESSAGE;
      throw new SuiteApiError(errorMessage, response.status, errorDetails, throttle);
    }
    if (response.status === 204) return {} as T;
    if (parseOk) return parseOk(response);
    return response.json();
  }

  private async executeWithRetry<T>(
    fetchFn: (authHeaders: HeadersInit) => Promise<Response>,
    skipAuth?: boolean,
    hasRetried = false,
    parseOk?: (response: Response) => Promise<T>
  ): Promise<T> {
    const makeHeaders = async (): Promise<HeadersInit> =>
      skipAuth ? {} : await this.authHeaders();

    let response = await fetchFn(await makeHeaders());

    if (!response.ok && !skipAuth && !hasRetried) {
      let shouldRefresh = false;

      if (response.status === 401) {
        shouldRefresh = true;
      } else if (response.status === 403) {
        // Only refresh if the body explicitly signals an expired access token.
        // Backend returns {"detail": "Access token has expired"} — no code field.
        // Require both substrings to avoid matching permission/role/csrf errors.
        try {
          const data = await response.clone().json();
          const detail = data?.detail?.toLowerCase?.() ?? "";
          shouldRefresh = detail.includes("access token") && detail.includes("expired");
        } catch {
          /* non-JSON 403 — permission/role/tenant denial, don't refresh */
        }
      }

      if (shouldRefresh) {
        const refreshed = await forceRefreshToken();
        if (refreshed) {
          response = await fetchFn(await makeHeaders());
        }
        // If refresh failed, fall through — handleResponse will redirect on 401
      }
    }

    return this.handleResponse<T>(response, skipAuth, parseOk);
  }

  async get<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { skipAuth, ...init } = options;
    const url = `${this.baseUrl}${path}`;
    return this.executeWithRetry(
      async (authHeaders) =>
        fetch(url, {
          ...init,
          method: "GET",
          headers: { "Content-Type": "application/json", ...authHeaders, ...init.headers },
        }),
      skipAuth
    );
  }

  /**
   * GET a binary resource (e.g. a generated PDF/DOCX export) as a Blob.
   *
   * Shares the auth + 401/403-refresh-retry path with `get`, but parses a
   * successful response with `response.blob()` instead of `response.json()`.
   * Error responses (incl. a 502 `{ "error": "…" }` from a failed conversion)
   * still flow through the JSON error path and surface as `SuiteApiError`, so
   * callers never receive a corrupt/partial Blob.
   */
  async getBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
    const { skipAuth, ...init } = options;
    const url = `${this.baseUrl}${path}`;
    return this.executeWithRetry<Blob>(
      async (authHeaders) =>
        fetch(url, {
          ...init,
          method: "GET",
          headers: { ...authHeaders, ...init.headers },
        }),
      skipAuth,
      false,
      (response) => response.blob()
    );
  }

  async post<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    const { skipAuth, ...init } = options;
    const url = `${this.baseUrl}${path}`;
    const bodyStr = body ? JSON.stringify(body) : undefined;
    return this.executeWithRetry(
      async (authHeaders) =>
        fetch(url, {
          ...init,
          method: "POST",
          headers: { "Content-Type": "application/json", ...authHeaders, ...init.headers },
          body: bodyStr,
        }),
      skipAuth
    );
  }

  async put<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    const { skipAuth, ...init } = options;
    const url = `${this.baseUrl}${path}`;
    const bodyStr = body ? JSON.stringify(body) : undefined;
    return this.executeWithRetry(
      async (authHeaders) =>
        fetch(url, {
          ...init,
          method: "PUT",
          headers: { "Content-Type": "application/json", ...authHeaders, ...init.headers },
          body: bodyStr,
        }),
      skipAuth
    );
  }

  async patch<T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> {
    const { skipAuth, ...init } = options;
    const url = `${this.baseUrl}${path}`;
    const bodyStr = body ? JSON.stringify(body) : undefined;
    return this.executeWithRetry(
      async (authHeaders) =>
        fetch(url, {
          ...init,
          method: "PATCH",
          headers: { "Content-Type": "application/json", ...authHeaders, ...init.headers },
          body: bodyStr,
        }),
      skipAuth
    );
  }

  async delete<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { skipAuth, ...init } = options;
    const url = `${this.baseUrl}${path}`;
    return this.executeWithRetry(
      async (authHeaders) =>
        fetch(url, {
          ...init,
          method: "DELETE",
          headers: { "Content-Type": "application/json", ...authHeaders, ...init.headers },
        }),
      skipAuth
    );
  }

  async upload<T>(path: string, formData: FormData, options: RequestOptions = {}): Promise<T> {
    const { skipAuth, ...init } = options;
    const url = `${this.baseUrl}${path}`;
    return this.executeWithRetry(
      async (authHeaders) =>
        fetch(url, {
          ...init,
          method: "POST",
          headers: { ...authHeaders, ...init.headers },
          body: formData,
        }),
      skipAuth
    );
  }

  /**
   * POST a multipart upload (e.g. a client-generated DOCX handed to the
   * backend for a format conversion) and receive a binary response as a
   * Blob — the multipart-request counterpart to `getBlob`.
   */
  async uploadBlob(path: string, formData: FormData, options: RequestOptions = {}): Promise<Blob> {
    const { skipAuth, ...init } = options;
    const url = `${this.baseUrl}${path}`;
    return this.executeWithRetry<Blob>(
      async (authHeaders) =>
        fetch(url, {
          ...init,
          method: "POST",
          headers: { ...authHeaders, ...init.headers },
          body: formData,
        }),
      skipAuth,
      false,
      (response) => response.blob()
    );
  }
}
